import React from 'react';
import { Ionicons } from '@expo/vector-icons';
import { Text, TextInput, TouchableOpacity, View } from 'react-native';
import { ticketColors } from '../../shared/theme/ticketColors';
import { catalogProductFormStyles as styles } from './catalogProductForm.styles';
import { CatalogProductFormSuggestion } from './CatalogProductFormSuggestion';
import type { CatalogProductFormFieldsProps } from './catalogProductFormFields.types';

type Props = Pick<
  CatalogProductFormFieldsProps,
  | 'draft'
  | 'onBarcodeChange'
  | 'onSkuChange'
  | 'onOpenScanner'
  | 'showBarcodeCameraButton'
  | 'onBarcodeSubmitEditing'
  | 'barcodeValidationMessage'
  | 'codeValidationMessage'
  | 'formSuggestion'
  | 'mobile'
  | 'desktopRail'
  | 'desktopQuickMode'
  | 'barcodeInputRef'
> & {
  compact?: boolean;
};

export function CatalogProductFormCodesFields({
  draft,
  onBarcodeChange,
  onSkuChange,
  onOpenScanner,
  showBarcodeCameraButton = true,
  onBarcodeSubmitEditing,
  barcodeValidationMessage,
  codeValidationMessage,
  formSuggestion,
  mobile = false,
  desktopRail = false,
  desktopQuickMode = false,
  barcodeInputRef,
  compact = false,
}: Props) {
  const showScan = showBarcodeCameraButton;
  const scannerMethods = [onBarcodeSubmitEditing ? 'la pistola' : null, showScan ? 'la cámara' : null].filter(Boolean);
  const barcodeHint = scannerMethods.length
    ? `Escaneá el código con ${scannerMethods.join(' o ')}. También podés escribirlo a mano.`
    : 'Escribí el código de barras.';
  const lookupHint = 'Al escanearlo, buscamos datos y, si encontramos, completamos nombre y marca. Revisalos antes de guardar.';

  if (compact) {
    return (
      <View style={styles.ticketFormSection}>
        {!desktopQuickMode ? <Text style={styles.ticketFormSectionTitle}>Código de barras</Text> : null}
        <View style={styles.ticketFormField}>
          <Text style={styles.ticketFormLabel}>Código de barras</Text>
          <View style={styles.ticketInpRow}>
            <TextInput
              ref={barcodeInputRef}
              style={[
                styles.ticketFormInput,
                styles.ticketInpRowInput,
                desktopQuickMode && styles.quickBarcodeInput,
              ]}
              value={draft.barcode}
              onChangeText={onBarcodeChange}
              placeholder="779…"
              placeholderTextColor={ticketColors.ink3}
              autoCapitalize="characters"
              keyboardType="default"
              accessibilityLabel="Código de barras del producto"
              testID="catalog.form.barcode"
              onSubmitEditing={onBarcodeSubmitEditing}
              selectTextOnFocus
              blurOnSubmit={false}
            />
            {showScan ? (
              <TouchableOpacity
                style={[styles.ticketScanBtn, desktopQuickMode && styles.quickScanButton]}
                onPress={onOpenScanner}
                accessibilityRole="button"
                accessibilityLabel="Escanear código de barras"
                testID="catalog.form.barcode.scan"
              >
                <Ionicons name="barcode-outline" size={18} color={ticketColors.ink2} />
              </TouchableOpacity>
            ) : null}
          </View>
          {desktopQuickMode && scannerMethods.length > 0 ? <Text style={styles.ticketFormHint}>{barcodeHint}</Text> : null}
          {barcodeValidationMessage ? (
            <Text style={[styles.inputHelper, styles.inputHelperError]}>{barcodeValidationMessage}</Text>
          ) : null}
        </View>
        {desktopQuickMode ? <CatalogProductFormSuggestion formSuggestion={formSuggestion} /> : null}
      </View>
    );
  }

  return (
    <View style={styles.ticketFormSection}>
      {!desktopRail ? <Text style={styles.ticketFormSectionTitle}>Códigos</Text> : null}
      {/* Barcode + SKU: grid on desktop, stack on mobile */}
      <View style={mobile ? styles.fieldGridMobile : styles.fieldGrid}>
        <View style={[styles.ticketFormField, !mobile && styles.fieldGridItem]}>
          <Text style={styles.ticketFormLabel}>Código de barras</Text>
          <View style={styles.ticketInpRow}>
            <TextInput
              style={[styles.ticketFormInput, styles.ticketInpRowInput]}
              value={draft.barcode}
              onChangeText={onBarcodeChange}
              placeholder="779…"
              placeholderTextColor={ticketColors.ink3}
              autoCapitalize="characters"
              keyboardType="default"
              accessibilityLabel="Código de barras del producto"
              testID="catalog.form.barcode"
              onSubmitEditing={onBarcodeSubmitEditing}
              selectTextOnFocus
              blurOnSubmit={false}
            />
            {showScan ? (
              <TouchableOpacity
                style={[styles.ticketScanBtn, desktopQuickMode && styles.quickScanButton]}
                onPress={onOpenScanner}
                accessibilityRole="button"
                accessibilityLabel="Escanear código de barras"
                testID="catalog.form.barcode.scan"
              >
                <Ionicons name="barcode-outline" size={18} color={ticketColors.ink2} />
              </TouchableOpacity>
            ) : null}
          </View>
          {barcodeValidationMessage ? (
            <Text style={[styles.inputHelper, styles.inputHelperError]}>{barcodeValidationMessage}</Text>
          ) : null}
        </View>
        <View style={[styles.ticketFormField, !mobile && styles.fieldGridItem]}>
          <Text style={styles.ticketFormLabel}>SKU</Text>
          <TextInput
            style={styles.ticketFormInput}
            value={draft.sku}
            onChangeText={onSkuChange}
            placeholder="Opcional"
            placeholderTextColor={ticketColors.ink3}
            autoCapitalize="characters"
            testID="catalog.form.sku"
          />
          {codeValidationMessage ? (
            <Text style={[styles.inputHelper, styles.inputHelperError]}>{codeValidationMessage}</Text>
          ) : null}
        </View>
      </View>
      {desktopRail && scannerMethods.length > 0 ? <Text style={styles.ticketFormHint}>{lookupHint}</Text> : null}
    </View>
  );
}
